import { localizePath } from "@plaatplan/i18n";
import { pickFaqItems } from "@plaatplan/i18n";
import { useState } from "react";
import { isMobileAppPromoLandingEnabled } from "../config/featureFlags";
import { LocalizedLink } from "../i18n/LocalizedLink";
import { SeoHead } from "../seo/SeoHead";
import { useInfoPages } from "../i18n/useInfoPages";
import { useLandingHighlights, useLandingPages } from "../i18n/useLandingPages";
import { DismissibleMobileAppPromo } from "../promo/DismissibleMobileAppPromo";
import { InfoPageModal } from "../ui/InfoPageModal";
import { LandingPlanPreview } from "../ui/LandingPlanPreview";
import { SiteFooter } from "../ui/SiteFooter";
import { SiteHeader } from "../ui/SiteHeader";
import { useLocale } from "../i18n/useLocale";

export function LandingPage({ slug }) {
  const { display, preferences } = useLocale();
  const landingPages = useLandingPages();
  const highlights = useLandingHighlights();
  const page = landingPages.find((item) => item.slug === slug);
  const infoPages = useInfoPages();
  const { how } = infoPages;
  const faq = page ? pickFaqItems(infoPages, page.faqIds) : [];
  const [infoPage, setInfoPage] = useState(null);
  const showAppPromo = isMobileAppPromoLandingEnabled();

  if (!page) return null;

  const pagePath = localizePath(page.path, preferences.locale);

  return (
    <div className="page page--landing">
      <SeoHead
        title={page.title}
        description={page.description}
        path={pagePath}
        faq={faq}
        breadcrumb={[
          { name: "Plaatplan", path: localizePath("/", preferences.locale) },
          { name: page.h1, path: pagePath },
        ]}
      />
      <a className="skip-link" href="#landing-main">
        {display.t("web.shell.skipLink")}
      </a>
      <SiteHeader />

      <main id="landing-main" className="landing-main" tabIndex={-1}>
        <section className="seo-landing landing-hero" aria-labelledby="landing-h1">
          <div className="seo-landing-inner">
            <header className="seo-landing-intro">
              <h1 id="landing-h1">{page.h1}</h1>
              <p className="seo-landing-lead">{page.lead}</p>
              <ul className="landing-highlights">
                {highlights.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <p className="landing-cta-row">
                <LocalizedLink to="/" className="primary-button landing-cta">
                  {page.ctaLabel}
                </LocalizedLink>
              </p>
            </header>

            <LandingPlanPreview />

            <div className="seo-landing-grid">
              {page.sections.map((section) => (
                <article key={section.h2} className="seo-landing-block">
                  <h2>{section.h2}</h2>
                  {section.paragraphs.map((paragraph) => (
                    <p key={paragraph.slice(0, 40)}>{paragraph}</p>
                  ))}
                </article>
              ))}
            </div>

            <article className="seo-landing-block landing-how" id="zo-werkt-plaatplan">
              <h2>{display.t("web.landing.howItWorks")}</h2>
              <p>{how.intro}</p>
              <ol className="seo-landing-steps">
                {how.steps.map((step) => (
                  <li key={step.title}>
                    <strong>{step.title}</strong>
                    <span>{step.body}</span>
                  </li>
                ))}
              </ol>
              <p className="landing-cta-row landing-cta-row--single">
                <LocalizedLink to="/" className="secondary-button landing-cta">
                  {page.ctaLabel}
                </LocalizedLink>
              </p>
            </article>

            {showAppPromo ? (
              <DismissibleMobileAppPromo layout="tablet" variant="planner" />
            ) : null}

            {faq.length > 0 && (
              <article className="seo-landing-block" id="veelgestelde-vragen">
                <h2>{display.t("web.landing.faq")}</h2>
                <div className="seo-faq-list">
                  {faq.map((item) => (
                    <details key={item.question} className="seo-faq-item">
                      <summary>{item.question}</summary>
                      <p>{item.answer}</p>
                    </details>
                  ))}
                </div>
              </article>
            )}
          </div>
        </section>
      </main>

      <SiteFooter onOpenPage={setInfoPage} />

      {infoPage && (
        <InfoPageModal
          pageId={infoPage}
          page={infoPages[infoPage]}
          onClose={() => setInfoPage(null)}
        />
      )}
    </div>
  );
}
